import { useState, useMemo, useEffect } from 'react';
import ClientPortalFunds from './ClientPortalFunds';
import { SummaryCard } from '../components/UI';
import PortfolioExposure from '../components/PortfolioExposure';
import { fmtMoney } from '../parsers/xmlParser';
import { computeHonKitzba } from '../utils/honKitzba';
import { i18n } from './portalI18n';
import DashboardCharts from '../components/DashboardCharts';

import { HonModal, KitzbaModal } from '../components/ReportModals';
import * as XLSX from 'xlsx';
import { pitzuyimFieldsForFund, getFundClass } from '../utils/reportComputations';

/**
 * PortalDashboard — read-only view of a single client's holdings.
 * Intentionally excludes: retirement planning, split-screen, activity log,
 * advisor-only actions, and any upload/import controls.
 */
export default function PortalDashboard({ familyMembers, advisorLogo, lang, onSignOut }) {
  const t = i18n[lang];
  const [filterStatus, setFilterStatus] = useState('הכל');
  const [search, setSearch] = useState('');
  const [showCharts, setShowCharts] = useState(true);
  
  const [showHonModal, setShowHonModal] = useState(false);
  const [showKitzbaModal, setShowKitzbaModal] = useState(false);
  
  // Default to the first member's ID, or 'all' if there are multiple members
  const [activeTab, setActiveTab] = useState(familyMembers.length > 1 ? 'all' : (familyMembers[0]?.clientTaz || 'all'));

  const activeData = useMemo(() => {
    let funds = [];
    let history = [];
    let name = '';
    let taarichBitzua = null;

    if (activeTab === 'all') {
      name = 'משפחתי (סה״כ)';
      familyMembers.forEach(m => {
        let allFunds = m.funds || [];
        const isPropFund = (f) => ['manual', 'prat'].includes(f._sourceType);
        
        let activeMonth = null;
        if (m.history && m.history.length > 0) {
          const periods = m.history.map(s => s.period);
          activeMonth = periods.sort().reverse()[0];
        } else {
          // Fallback to max prat month if no history
          allFunds.forEach(f => {
            if (f.validDate && f._sourceType === 'prat') {
              const parts = f.validDate.split('/');
              const iso = parts.length === 3 ? `${parts[2]}-${parts[1]}` : (parts.length === 2 ? `${parts[1]}-${parts[0]}` : null);
              if (iso && (!activeMonth || iso > activeMonth)) activeMonth = iso;
            }
          });
        }
        
        const selectedSnapshot = m.history ? m.history.find(s => s.period === activeMonth) : null;
        
        let maslekaFunds = allFunds.filter(f => !isPropFund(f));
        let propFunds = allFunds.filter(f => isPropFund(f));

        if (selectedSnapshot) {
          const liveKey = f => f.id || `${f.companyCode || ""}_${f.polNum || ""}_${f.kidodAchid || ""}`;
          const byKey = new Set(selectedSnapshot.perFund.map(pf => pf.key));
          maslekaFunds = maslekaFunds.filter(f => byKey.has(liveKey(f)));
        } else if (activeMonth && maslekaFunds.length > 0) {
          // If masleka exists but no snapshot? shouldn't happen, but fallback just in case
        }

        if (activeMonth) {
          propFunds = propFunds.filter(f => {
            if (f._sourceType === 'manual') return true;
            let iso = null;
            if (f.validDate) {
              const parts = f.validDate.split('/');
              if (parts.length === 3) iso = `${parts[2]}-${parts[1]}`;
              else if (parts.length === 2) iso = `${parts[1]}-${parts[0]}`;
            }
            return iso === activeMonth;
          });
        }

        const memFunds = [...maslekaFunds, ...propFunds].map(f => ({ ...f, _ownerName: m.clientName }));
        funds = funds.concat(memFunds);
        if (m.fileInfo?.taarichBitzua && (!taarichBitzua || new Date(m.fileInfo.taarichBitzua) > new Date(taarichBitzua))) {
          taarichBitzua = m.fileInfo.taarichBitzua;
        }
      });
      // We could merge history, but for simplicity, we pass empty array for "all" for now
    } else {
      const mem = familyMembers.find(m => m.clientTaz === activeTab);
      if (mem) {
        name = mem.clientName;
        let allFunds = mem.funds || [];
        const isPropFund = (f) => ['manual', 'prat'].includes(f._sourceType);
        
        let activeMonth = null;
        if (mem.history && mem.history.length > 0) {
          const periods = mem.history.map(s => s.period);
          activeMonth = periods.sort().reverse()[0];
        } else {
          allFunds.forEach(f => {
            if (f.validDate && f._sourceType === 'prat') {
              const parts = f.validDate.split('/');
              const iso = parts.length === 3 ? `${parts[2]}-${parts[1]}` : (parts.length === 2 ? `${parts[1]}-${parts[0]}` : null);
              if (iso && (!activeMonth || iso > activeMonth)) activeMonth = iso;
            }
          });
        }
        
        const selectedSnapshot = mem.history ? mem.history.find(s => s.period === activeMonth) : null;
        
        let maslekaFunds = allFunds.filter(f => !isPropFund(f));
        let propFunds = allFunds.filter(f => isPropFund(f));

        if (selectedSnapshot) {
          const liveKey = f => f.id || `${f.companyCode || ""}_${f.polNum || ""}_${f.kidodAchid || ""}`;
          const byKey = new Set(selectedSnapshot.perFund.map(pf => pf.key));
          maslekaFunds = maslekaFunds.filter(f => byKey.has(liveKey(f)));
        }

        if (activeMonth) {
          propFunds = propFunds.filter(f => {
            if (f._sourceType === 'manual') return true;
            let iso = null;
            if (f.validDate) {
              const parts = f.validDate.split('/');
              if (parts.length === 3) iso = `${parts[2]}-${parts[1]}`;
              else if (parts.length === 2) iso = `${parts[1]}-${parts[0]}`;
            }
            return iso === activeMonth;
          });
        }

        funds = [...maslekaFunds, ...propFunds];
        history = mem.history || [];
        taarichBitzua = mem.fileInfo?.taarichBitzua;
      }
    }
    return { funds, history, name, taarichBitzua };
  }, [familyMembers, activeTab]);

  const { funds, history, name: clientName, taarichBitzua: updatedAtRaw } = activeData;

  const totals = useMemo(() => {
    let tagmulimHon = 0, tagmulimKitzba = 0, pitzuyimKitzba = 0;
    (funds || []).forEach(f => {
      const { hon, kitzba, kitzbaPitzuyim, preReform, kitzbaPreSettlement } = computeHonKitzba(f);
      const kitzbaUntil2000 = (preReform || 0) + (kitzbaPreSettlement || 0);

      tagmulimHon    += hon + kitzbaUntil2000;
      tagmulimKitzba += (kitzba || 0) - (kitzbaPreSettlement || 0);
      pitzuyimKitzba += kitzbaPitzuyim || 0;
    });
    return {
      redeem:  (funds || []).reduce((s, f) => s + (f.redeem || 0), 0),
      pension: (funds || []).reduce((s, f) => {
        const p = f.retirementProjections?.[0]?.kupot?.[0]?.pensionMonthly
               || f.retirementProjections?.[0]?.kupot?.[0]?.pension || 0;
        return s + (parseFloat(p) || 0);
      }, 0),
      bituach: (funds || []).reduce((s, f) => s + (f.kisuim?.[0]?.bituachLemavet || 0), 0),
      alerts:  (funds || []).filter(f => f.debts || f.hasShiabud || f.loan).length,
      tagmulimHon,
      tagmulimKitzba: tagmulimKitzba + pitzuyimKitzba,
    };
  }, [funds]);

  const statuses = useMemo(() =>
    ['הכל', ...new Set((funds || []).map(f => f.statusLabel || 'פעיל'))],
    [funds]
  );

  const filtered = useMemo(() => {
    return (funds || [])
      .filter(f => {
        const matchStatus = filterStatus === 'הכל' || f.statusLabel === filterStatus;
        const q = search.trim();
        const matchSearch = !q || [f.companyName, f.tochnit, f.polNum]
          .some(v => v?.includes(q));
        return matchStatus && matchSearch;
      })
      .sort((a, b) => (b.redeem || 0) - (a.redeem || 0));
  }, [funds, filterStatus, search]);

  const updatedAt = updatedAtRaw
    ? new Date(updatedAtRaw).toLocaleDateString('he-IL')
    : null;

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Jerusalem',
    hour: 'numeric',
    hour12: false,
  });
  const hour = parseInt(timeFormatter.format(new Date()), 10);
  const greeting = hour < 12 ? 'בוקר טוב' : hour < 18 ? 'צהריים טובים' : 'ערב טוב';
  const firstName = clientName.split(' ')[0];

  const handleExportExcel = () => {
    const listToExport = filtered?.length > 0 ? filtered : (funds || []);
    if (!listToExport || listToExport.length === 0) {
      alert('אין קופות לייצוא');
      return;
    }

    const isFamily = activeTab === 'all';

    let totalHon = 0;
    let totalKitzbaMesh = 0;
    let totalKitzbaLoMesh = 0;
    let totalPitzuyimHon = 0;
    let totalPitzuyimKitzbaMesh = 0;
    let totalPitzuyimKitzbaLoMesh = 0;

    const dataRows = listToExport.map(f => {
      const type = getFundClass(f);
      const hk = computeHonKitzba(f);
      const pitz = pitzuyimFieldsForFund(f);
      const isStudy = f.cat === 'קרן השתלמות';

      // הון
      const hon = Math.round(isStudy ? (f.redeem || hk.hon || 0) : ((hk.hon || 0) + (hk.preReform || 0) + (hk.kitzbaPreSettlement || 0)));

      // קצבה תגמולים
      const kitzbaNeto = Math.round((hk.kitzba || 0) - (hk.kitzbaPreSettlement || 0));
      const kitzbaMesh = type === 'קצבה משלמת' ? kitzbaNeto : 0;
      const kitzbaLoMesh = type === 'קצבה לא משלמת' ? kitzbaNeto : 0;

      // פיצויים
      const pitzHon = Math.round(pitz.honAmount || 0);
      const pitzKitzbaMesh = Math.round(pitz.kitzbaMeshAmount || 0);
      const pitzKitzbaLoMesh = Math.round(pitz.kitzbaLoMeshAmount || 0);

      totalHon += hon;
      totalKitzbaMesh += kitzbaMesh;
      totalKitzbaLoMesh += kitzbaLoMesh;
      totalPitzuyimHon += pitzHon;
      totalPitzuyimKitzbaMesh += pitzKitzbaMesh;
      totalPitzuyimKitzbaLoMesh += pitzKitzbaLoMesh;

      // מסלול
      const maslulNames = (f.maslulim || []).map(m => m.name).filter(Boolean);
      const maslulStr = maslulNames.length > 0
        ? Array.from(new Set(maslulNames)).join(', ')
        : (f.maslul || f.maslulName || 'כללי');

      // שם קופה
      const shemKupa = f.tochnit
        ? (f.companyName && !f.tochnit.includes(f.companyName) ? `${f.companyName} - ${f.tochnit}` : f.tochnit)
        : (f.companyName || '—');

      // תשואה
      const tsuaRaw = f.returns?.neto ?? f.returns?.bruto1 ?? f.returns?.bruto2 ?? null;
      const tsua = tsuaRaw != null ? `${tsuaRaw}%` : '—';

      const row = {};
      if (isFamily) {
        row['שם עמית'] = f._ownerName || clientName || '—';
      }
      row['תאריך נכונות'] = f.validDate || updatedAt || '—';
      row['סוג קופה'] = f.cat || '—';
      row['שם קופה'] = shemKupa;
      row['מסלול'] = maslulStr;
      row['מספר חשבון'] = f.polNum || '—';
      row['הון'] = hon;
      row['קצבה משלמת'] = kitzbaMesh;
      row['קצבה לא משלמת'] = kitzbaLoMesh;
      row['פיצויים בהון'] = pitzHon;
      row['פיצויים קצבה משלמת'] = pitzKitzbaMesh;
      row['פיצויים קצבה לא משלמת'] = pitzKitzbaLoMesh;
      row['תשואה'] = tsua;

      return row;
    });

    // שורת סיכום
    const summaryRow = {};
    if (isFamily) {
      summaryRow['שם עמית'] = 'סה"כ';
      summaryRow['תאריך נכונות'] = '';
    } else {
      summaryRow['תאריך נכונות'] = 'סה"כ';
    }
    summaryRow['סוג קופה'] = '';
    summaryRow['שם קופה'] = `${listToExport.length} קופות`;
    summaryRow['מסלול'] = '';
    summaryRow['מספר חשבון'] = '';
    summaryRow['הון'] = totalHon;
    summaryRow['קצבה משלמת'] = totalKitzbaMesh;
    summaryRow['קצבה לא משלמת'] = totalKitzbaLoMesh;
    summaryRow['פיצויים בהון'] = totalPitzuyimHon;
    summaryRow['פיצויים קצבה משלמת'] = totalPitzuyimKitzbaMesh;
    summaryRow['פיצויים קצבה לא משלמת'] = totalPitzuyimKitzbaLoMesh;
    summaryRow['תשואה'] = '';

    const exportRows = [...dataRows, summaryRow];

    const ws = XLSX.utils.json_to_sheet(exportRows);
    ws['!views'] = [{ rightToLeft: true }];
    const colKeys = Object.keys(exportRows[0] || {});
    ws['!cols'] = colKeys.map(k => ({
      wch: Math.max(k.length * 2, 14),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'דוח קופות');

    const dateStr = new Date().toISOString().slice(0, 10);
    const filename = isFamily
      ? `דוח_קופות_משפחתי_${dateStr}.xlsx`
      : `דוח_קופות_${(clientName || 'עמית').trim().replace(/\s+/g, '_')}_${dateStr}.xlsx`;

    XLSX.writeFile(wb, filename);
  };


  return (
    <div dir={i18n[lang].dir} style={{
      minHeight: '100vh', background: '#f1f5f9',
      fontFamily: "inherit",
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        color: 'white', padding: '16px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {advisorLogo ? (
            <img src={advisorLogo} alt="Advisor" style={{ height: 40, width: 40, objectFit: 'contain', background: 'white', borderRadius: 8, padding: 2 }} />
          ) : (
            <img src="/mivida-logo.png" alt="MIVIDA" style={{ height: 32, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
          )}
          <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{clientName || fileInfo?.client}</div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
              {updatedAt ? `עודכן לאחרונה: ${updatedAt}` : 'התיק הפנסיוני שלך'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={handleExportExcel}
            style={{
              background: '#16a34a', border: '1px solid #15803d',
              color: 'white', padding: '6px 14px', borderRadius: 8,
              cursor: 'pointer', fontSize: 13, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}
          >
            <span>📥</span>
            <span>ייצוא לאקסל</span>
          </button>
          <button
            onClick={onSignOut}
            style={{
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', padding: '6px 14px', borderRadius: 8,
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            {t.logout}
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 20px 0', maxWidth: 800, margin: '0 auto' }}>
        
        {/* Hero Section */}
        <div style={{ background: 'white', borderRadius: 16, padding: '24px 20px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', marginBottom: 24, textAlign: 'center' }}>
          
          {familyMembers.length > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
              <button
                onClick={() => setActiveTab('all')}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: activeTab === 'all' ? 700 : 500,
                  background: activeTab === 'all' ? '#1e293b' : '#f1f5f9',
                  color: activeTab === 'all' ? 'white' : '#64748b', border: 'none', cursor: 'pointer',
                  boxShadow: activeTab === 'all' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                הכל (משפחתי)
              </button>
              {familyMembers.map(m => {
                const isMain = m.relationType === 'main';
                const isSpouse = m.relationType === 'spouse';
                const isChild = m.relationType === 'child';
                const icon = isMain ? '👤' : isSpouse ? '👩‍❤️‍👨' : isChild ? '👦' : '🔗';
                return (
                <button
                  key={m.clientTaz}
                  onClick={() => setActiveTab(m.clientTaz)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: activeTab === m.clientTaz ? 700 : 500,
                    background: activeTab === m.clientTaz ? '#4338ca' : '#f1f5f9',
                    color: activeTab === m.clientTaz ? 'white' : '#64748b', border: 'none', cursor: 'pointer',
                    boxShadow: activeTab === m.clientTaz ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 6
                  }}
                >
                  <span style={{ fontSize: 14 }}>{icon}</span>
                  <span>{m.clientName.split(' ')[0]}</span>
                </button>
              )})}
            </div>
          )}

          <div style={{ fontSize: 16, color: '#64748b', marginBottom: 4 }}>{greeting}, {familyMembers[0]?.clientName.split(' ')[0]} 👋</div>
          <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 20 }}>
            {activeTab === 'all' ? 'ריכזנו עבורך את תמונת המצב הפנסיונית המשפחתית הכוללת נכון להיום' : `ריכזנו עבורך את תמונת המצב הפנסיונית של ${clientName} נכון להיום`}
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, background: '#f8fafc', padding: '16px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
             <div>
               <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>סה״כ צבירה נוכחית</div>
               <div style={{ fontSize: 32, fontWeight: 800, color: '#4338ca' }}>{fmtMoney(totals.redeem)}</div>
             </div>
             {/* 
             <div>
               <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>קצבה חודשית צפויה</div>
               <div style={{ fontSize: 26, fontWeight: 800, color: '#16a34a' }}>{fmtMoney(totals.pension)}</div>
             </div>
             */}
          </div>
          
          <div style={{ display: 'flex', gap: 6, marginTop: 16, justifyContent: 'space-between', flexWrap: 'nowrap' }}>
             <div onClick={() => setShowHonModal(true)} title="סך הכל כספים המיועדים למשיכה כסכום חד-פעמי פטור ממס (בכפוף לתקרות)" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '6px 4px', borderRadius: 8, fontSize: 12, color: '#1e40af', cursor: 'pointer', flex: 1 }}>
               <span style={{ fontWeight: 600 }}>הון:</span> {fmtMoney(totals.tagmulimHon)}
             </div>
             <div onClick={() => setShowKitzbaModal(true)} title="סך הכל כספים המיועדים לקצבה חודשית (פנסיה) לגיל הפרישה" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '6px 4px', borderRadius: 8, fontSize: 12, color: '#166534', cursor: 'pointer', flex: 1 }}>
               <span style={{ fontWeight: 600 }}>קצבה:</span> {fmtMoney(totals.tagmulimKitzba)}
             </div>
             <div title="סך הכל כיסוי למקרה פטירה חלילה" style={{ background: '#fff1f2', border: '1px solid #fecdd3', padding: '6px 4px', borderRadius: 8, fontSize: 12, color: '#be123c', cursor: 'help', flex: 1 }}>
               <span style={{ fontWeight: 600 }}>חיים:</span> {fmtMoney(totals.bituach)}
             </div>
          </div>

          {/* Quick Export in Hero Card */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
            <button
              onClick={handleExportExcel}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#16a34a', color: 'white', border: 'none',
                padding: '8px 20px', borderRadius: 10, cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.25)',
              }}
            >
              <span>📥</span>
              <span>{activeTab === 'all' ? 'ייצוא דוח משפחתי לאקסל' : `ייצוא דוח ${firstName} לאקסל`}</span>
            </button>
          </div>
        </div>

        {/* Exposure */}
        <PortfolioExposure funds={funds || []} />

        {/* Charts toggle & Excel export */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
          <div
            onClick={() => setShowCharts(v => !v)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
              fontSize: 13, fontWeight: 700, color: "#1e40af",
              userSelect: "none",
            }}
          >
            <span style={{ fontSize: 15 }}>{showCharts ? "📋" : "📊"}</span>
            <span>{showCharts ? "הסתר גרפים ▲" : "הצג גרפים ▼"}</span>
          </div>

          <button
            onClick={handleExportExcel}
            title={activeTab === 'all' ? "ייצוא כל הקופות של המשפחה לאקסל" : `ייצוא הקופות של ${clientName} לאקסל`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#16a34a", color: "white", border: "none",
              padding: "7px 14px", borderRadius: 10, cursor: "pointer",
              fontSize: 13, fontWeight: 700,
              boxShadow: "0 2px 6px rgba(22, 163, 74, 0.25)",
              transition: "all 0.15s ease",
            }}
          >
            <span>📥</span>
            <span>ייצוא לאקסל</span>
          </button>
        </div>
        {/* Charts panel */}
        {showCharts && (
          <DashboardCharts funds={funds || []} history={history} />
        )}

        {/* Search + filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 16 }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍  חיפוש חברה / מוצר..."
            style={{
              flex: 1, minWidth: 180, padding: '8px 12px',
              border: '1.5px solid #e2e8f0', borderRadius: 10,
              fontSize: 13, outline: 'none', direction: 'rtl', background: 'white',
            }}
          />
          {statuses.map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: '6px 12px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
              fontWeight: filterStatus === s ? 700 : 400,
              background: filterStatus === s ? '#4338ca' : 'white',
              border: `1.5px solid ${filterStatus === s ? '#4338ca' : '#e2e8f0'}`,
              color: filterStatus === s ? 'white' : '#475569',
            }}>{s}</button>
          ))}
        </div>

        {/* Funds List */}
        <div style={{ paddingBottom: 60 }}>
          <ClientPortalFunds funds={filtered} />
        </div>
      </div>

      {showHonModal && <HonModal funds={funds} onClose={() => setShowHonModal(false)} inline={false} />}
      {showKitzbaModal && <KitzbaModal funds={funds} onClose={() => setShowKitzbaModal(false)} inline={false} />}
    </div>
  );
}
